'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ReactNode } from 'react';
import { Button } from '@/components/shadcn/Button';
import { Dialog, DialogTrigger } from '@/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/shadcn/DropdownMenu';
import { LogoutDialog } from '@/components/dialog/LogoutDialog';
import useDialog from '@/hooks/useDialog';
import { HamburgerMenuIcon, CrossIcon } from 'public/static/images/icons';
import { sendEvent } from '@/lib/firebase/analytics/sendEvent';
import { EVENTS } from '@/lib/firebase/analytics/events';

const headerLinkItemStyle = 'md:text-xs text-[10px]';

const headerIconBasePath = '/static/images/icons/header';

type AssignLogoProps = {
  linkDisabled?: boolean;
  isLogin?: boolean;
};

export const AssignLogo = ({
  linkDisabled = false,
  isLogin = false
}: AssignLogoProps) => {
  return (
    <Link
      href={isLogin ? '/top' : '/'}
      className={`hover:text-primary mx-0 flex items-center space-x-4 text-[12px] font-medium transition-colors md:mx-5 lg:space-x-6 ${
        linkDisabled ? 'pointer-events-none' : ''
      }`}
    >
      <Image
        src="/static/images/icons/assign_tech_logo.svg"
        width={194}
        height={28}
        alt=""
        className="w-[168px] md:w-[194px]"
      />
      <p className="ml-6 mt-2 hidden text-xs md:block">
        IT特化の若手ハイエンド転職サイト
      </p>
    </Link>
  );
};

type HeaderDropdownMenuProps = {
  children: ReactNode;
};

export const HeaderDropdownMenu = ({
  children: headerDropdownMenuContent
}: HeaderDropdownMenuProps) => {
  // メニュー画面の開閉状態と切り替える関数を取得する
  const { open, onOpenChange } = useDialog();

  return (
    <Dialog>
      <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
        <DropdownMenuTrigger className="h-[72px] border-none focus:border-transparent focus:ring-0 focus-visible:outline-none">
          <div className="bg-transparent p-4 text-gray-600 hover:bg-gray-100">
            {open ? (
              <CrossIcon className="text-gray-400" size={24} />
            ) : (
              <HamburgerMenuIcon className="text-gray-400" size={24} />
            )}
          </div>
        </DropdownMenuTrigger>
        {/* globals.cssでstyle指定するため .header-menu-contentを挿入 */}
        <DropdownMenuContent className="header-menu-content max-h-[calc(100vh-64px)] overflow-y-auto rounded-none text-gray-400 md:mt-0 md:max-h-custom100vh">
          {headerDropdownMenuContent}
        </DropdownMenuContent>
      </DropdownMenu>
      <LogoutDialog />
    </Dialog>
  );
};

export const UserRegistration = () => {
  const pathname = usePathname();

  const redirectToRegisterPage = async () => {
    // Firebaseイベント発火
    await sendEvent(EVENTS.user_registration_start);
    location.href = '/register';
  };

  return (
    <Button
      variant="secondary"
      className="w-50 mx-0 h-full flex-col justify-center bg-white px-2 md:mr-4"
      onClick={redirectToRegisterPage}
    >
      <Image
        src={`/static/images/icons/header/register_${
          pathname === '/register' ? 'on' : 'off'
        }.svg`}
        width={34}
        height={34}
        alt=""
        className="w-[29px] md:w-[34px]"
      />
      <div
        className={`${headerLinkItemStyle} ${
          pathname === '/register' ? 'text-primary-400' : 'text-gray-400'
        }`}
      >
        会員登録
      </div>
    </Button>
  );
};

type BaseHeaderContentItemProps = {
  href: string;
  src: string;
  name: string;
};

const BaseHeaderContentItem = ({
  href,
  src,
  name
}: BaseHeaderContentItemProps) => {
  const pathname = usePathname();
  return (
    <Link href={href}>
      <Button
        variant="secondary"
        className="w-50 mx-0 h-full flex-col justify-center bg-white px-2 md:mr-4"
      >
        <Image
          src={src}
          width={36}
          height={36}
          alt=""
          className="w-[30px] md:w-[36px]"
        />
        <div
          className={`-mt-0.5 ${headerLinkItemStyle} ${
            pathname.includes(href) ? 'text-primary-400' : 'text-gray-400'
          }`}
        >
          {name}
        </div>
      </Button>
    </Link>
  );
};

export const Login = () => {
  const pathname = usePathname();
  return (
    <BaseHeaderContentItem
      href="/login"
      src={`${headerIconBasePath}/login_${
        pathname === '/login' ? 'on' : 'off'
      }.svg`}
      name="ログイン"
    />
  );
};

export const Message = () => {
  const pathname = usePathname();
  return (
    <BaseHeaderContentItem
      href="/chat-rooms"
      src={`${headerIconBasePath}/message_${
        pathname === '/chat-rooms' ? 'on' : 'off'
      }.svg`}
      name="メッセージ"
    />
  );
};

export const Scenario = () => {
  const pathname = usePathname();
  return (
    <BaseHeaderContentItem
      href="/scenarios"
      src={`${headerIconBasePath}/scenario_${
        pathname.includes('/scenarios') ? 'on' : 'off'
      }.svg`}
      name="シナリオ"
    />
  );
};

export const Learning = () => {
  const pathname = usePathname();
  return (
    <BaseHeaderContentItem
      href="/learning-courses"
      src={`${headerIconBasePath}/learning_${
        pathname.includes('/learning-courses') ? 'on' : 'off'
      }.svg`}
      name="ラーニング"
    />
  );
};
